import { Invoice } from "@/api/broker/getInvoices";
import {
  GetLoadForInvoice,
  getLoadsForInvoice,
} from "@/api/loads/getLoadsForInvoice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns/format";
import { Download } from "lucide-react";

interface Props {
  invoice: Invoice;
  setViewDialogOpen: (open: boolean) => void;
  generatePDF: (invoice: Invoice, loads: GetLoadForInvoice) => void;
  isSent: boolean;
}

export function InvoiceModalContent({
  invoice,
  setViewDialogOpen,
  generatePDF,
  isSent,
}: Props) {
  const { data: loadsForInvoice } = useQuery({
    queryKey: ["broker", "invoices", "loads", invoice.id],
    queryFn: () => getLoadsForInvoice({ invoiceId: invoice.id }),
    enabled: !!invoice.id,
  });

  const getStatusBadge = (status: string) => {
    const variants = {
      Paid: "bg-green-500",
      Pending: "bg-lupurple",
      Overdue: "bg-orange-300",
    } as const;

    return (
      <Badge
        className={variants[status as keyof typeof variants] || "bg-slate-400"}
      >
        {status}
      </Badge>
    );
  };
  return (
    <DialogContent className="max-w-3xl">
      <DialogHeader>
        <DialogTitle>Invoice Details - {invoice.name}</DialogTitle>
      </DialogHeader>

      {invoice && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Customer</Label>
              <div className="font-medium">{invoice.otherPartyName}</div>
            </div>
            <div>
              <Label>Status</Label>
              <div>{getStatusBadge(invoice.status)}</div>
            </div>
            <div>
              <Label>Invoice Date</Label>
              <div>{format(invoice.createdAt, "MM-dd-yy")}</div>
            </div>
            <div>
              <Label>Due Date</Label>
              <div>{format(invoice.dueDate, "MM-dd-yy")}</div>
            </div>
          </div>

          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Load Name</TableHead>
                  <TableHead>Carrier Info</TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead>Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadsForInvoice?.map((load) => (
                  <TableRow key={load.id}>
                    <TableCell className="font-medium">{load.name}</TableCell>
                    <TableCell>{`(${load.carrierName}) ${load.driverName}`}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {load.pickupLocation} → {load.deliveryLocation}
                      </div>
                    </TableCell>
                    {isSent ? (
                      <TableCell>
                        ${(load.chargeReceiver / 100).toFixed(2)}
                      </TableCell>
                    ) : (
                      <TableCell>
                        ${(load.chargeCarrier / 100).toFixed(2)}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="border-t pt-4">
            <div className="flex justify-between items-center text-lg font-semibold">
              <span>Total Amount:</span>
              <span>${(invoice.amountCents / 100).toFixed(2)}</span>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() =>
                loadsForInvoice?.forEach((load) => generatePDF(invoice, load))
              }
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </div>
        </div>
      )}
    </DialogContent>
  );
}
